import { useState, useEffect, useRef, useCallback } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload,
  X,
  Check,
  RefreshCw,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ImagePlus,
  Trash2,
  Loader2,
  PenLine,
  Stamp,
} from "lucide-react";

const BRAND_COLORS = [
  { name: "Blue", value: "#2563EB", label: "Default Blue" },
  { name: "Lime", value: "#99CC33", label: "Lime" },
  { name: "Amber", value: "#DE9A33", label: "Amber" },
  { name: "Rose", value: "#E11D48", label: "Rose" },
  { name: "Violet", value: "#7C3AED", label: "Violet" },
  { name: "Teal", value: "#0F766E", label: "Teal" },
] as const;

const DEFAULT_COLOR = BRAND_COLORS[0].value;

const MAX_AUTH_IMAGES = 6;

interface BrandingData {
  primaryColor: string;
  libraryFullName?: string | null;
  libraryAddress?: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  receiptSignatureUrl?: string | null;
  receiptStampUrl?: string | null;
  welcomeMessage?: string | null;
  customCss?: string | null;
  authImages?: string[];
}

const BrandingSettingsPage = () => {
  const { tenant, isLoading: isTenantLoading } = useTenant();
  const { isTenantAdmin } = useAccessControl();

  const [branding, setBranding] = useState<BrandingData>({
    primaryColor: DEFAULT_COLOR,
    authImages: [],
  });
  const [previewBranding, setPreviewBranding] = useState<BrandingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [isUploadingReceiptSignature, setIsUploadingReceiptSignature] = useState(false);
  const [isUploadingReceiptStamp, setIsUploadingReceiptStamp] = useState(false);
  const [isUploadingAuthImage, setIsUploadingAuthImage] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const receiptSignatureInputRef = useRef<HTMLInputElement>(null);
  const receiptStampInputRef = useRef<HTMLInputElement>(null);
  const authImageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tenant?.id) {
      loadBranding();
    } else if (!isTenantLoading) {
      setIsLoading(false);
      if (!tenant) {
        setError("Could not load tenant information. Please refresh the page.");
      }
    }
  }, [tenant?.id, isTenantLoading]);

  const loadBranding = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/tenants/${tenant?.id}/branding`);
      const data: BrandingData = {
        primaryColor: response.data.primaryColor || DEFAULT_COLOR,
        libraryFullName: response.data.libraryFullName ?? null,
        libraryAddress: response.data.libraryAddress ?? null,
        logoUrl: response.data.logoUrl,
        faviconUrl: response.data.faviconUrl,
        receiptSignatureUrl: response.data.receiptSignatureUrl ?? null,
        receiptStampUrl: response.data.receiptStampUrl ?? null,
        welcomeMessage: response.data.welcomeMessage,
        customCss: response.data.customCss,
        authImages: response.data.authImages || [],
      };
      setBranding(data);
      setPreviewBranding(null);
    } catch (err: unknown) {
      console.error("Failed to load branding:", err);
      setError("Failed to load branding settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleColorChange = (color: string) => {
    const updated = { ...branding, primaryColor: color };
    setBranding(updated);
    setPreviewBranding(updated);
  };

  const handleWelcomeMessageChange = (message: string) => {
    const updated = { ...branding, welcomeMessage: message };
    setBranding(updated);
    setPreviewBranding(updated);
  };

  const handleLibraryFullNameChange = (value: string) => {
    const updated = { ...branding, libraryFullName: value || null };
    setBranding(updated);
    setPreviewBranding(updated);
  };

  const handleLibraryAddressChange = (value: string) => {
    const updated = { ...branding, libraryAddress: value || null };
    setBranding(updated);
    setPreviewBranding(updated);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenant?.id) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Logo file size must be less than 5MB");
      return;
    }

    setIsUploadingLogo(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/tenants/${tenant.id}/branding/upload-logo`, formData);

      const updated = { ...branding, logoUrl: response.data.url };
      setBranding(updated);
      setPreviewBranding(updated);
      setSuccess("Logo uploaded successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as any)?.response?.data?.message || (err as any)?.message || "Failed to upload logo";
      setError(errorMessage);
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenant?.id) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 1024 * 1024) {
      setError("Favicon file size must be less than 1MB");
      return;
    }

    setIsUploadingFavicon(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/tenants/${tenant.id}/branding/upload-favicon`, formData);

      const updated = { ...branding, faviconUrl: response.data.url };
      setBranding(updated);
      setPreviewBranding(updated);
      setSuccess("Favicon uploaded successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as any)?.response?.data?.message ||
        (err as any)?.message ||
        "Failed to upload favicon";
      setError(errorMessage);
    } finally {
      setIsUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    if (!confirm("Are you sure you want to remove the logo?")) return;

    const updated = { ...branding, logoUrl: null };
    setBranding(updated);
    setPreviewBranding(updated);

    try {
      await api.put(`/tenants/${tenant?.id}/branding`, { ...updated, logoUrl: null });
      setSuccess("Logo removed successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      console.error("Failed to remove logo:", err);
      setError("Failed to remove logo");
    }
  };

  const handleRemoveFavicon = async () => {
    if (!confirm("Are you sure you want to remove the favicon?")) return;

    const updated = { ...branding, faviconUrl: null };
    setBranding(updated);
    setPreviewBranding(updated);

    try {
      await api.put(`/tenants/${tenant?.id}/branding`, { ...updated, faviconUrl: null });
      setSuccess("Favicon removed successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      console.error("Failed to remove favicon:", err);
      setError("Failed to remove favicon");
    }
  };

  const handleReceiptSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenant?.id) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image file size must be less than 5MB");
      return;
    }

    setIsUploadingReceiptSignature(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/tenants/${tenant.id}/branding/upload-receipt-signature`,
        formData
      );

      const updated = { ...branding, receiptSignatureUrl: response.data.url };
      setBranding(updated);
      setPreviewBranding(updated);
      setSuccess("Receipt signature uploaded!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as any)?.response?.data?.message ||
        (err as any)?.message ||
        "Failed to upload signature";
      setError(errorMessage);
    } finally {
      setIsUploadingReceiptSignature(false);
      if (receiptSignatureInputRef.current) receiptSignatureInputRef.current.value = "";
    }
  };

  const handleReceiptStampUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenant?.id) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image file size must be less than 5MB");
      return;
    }

    setIsUploadingReceiptStamp(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/tenants/${tenant.id}/branding/upload-receipt-stamp`,
        formData
      );

      const updated = { ...branding, receiptStampUrl: response.data.url };
      setBranding(updated);
      setPreviewBranding(updated);
      setSuccess("Receipt stamp uploaded!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as any)?.response?.data?.message || (err as any)?.message || "Failed to upload stamp";
      setError(errorMessage);
    } finally {
      setIsUploadingReceiptStamp(false);
      if (receiptStampInputRef.current) receiptStampInputRef.current.value = "";
    }
  };

  const handleRemoveReceiptSignature = async () => {
    if (!confirm("Remove the receipt signature image?")) return;

    const updated = { ...branding, receiptSignatureUrl: null };
    setBranding(updated);
    setPreviewBranding(updated);

    try {
      await api.put(`/tenants/${tenant?.id}/branding`, { ...updated, receiptSignatureUrl: null });
      setSuccess("Receipt signature removed.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      console.error("Failed to remove receipt signature:", err);
      setError("Failed to remove receipt signature");
    }
  };

  const handleRemoveReceiptStamp = async () => {
    if (!confirm("Remove the receipt stamp image?")) return;

    const updated = { ...branding, receiptStampUrl: null };
    setBranding(updated);
    setPreviewBranding(updated);

    try {
      await api.put(`/tenants/${tenant?.id}/branding`, { ...updated, receiptStampUrl: null });
      setSuccess("Receipt stamp removed.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      console.error("Failed to remove receipt stamp:", err);
      setError("Failed to remove receipt stamp");
    }
  };

  // ─── Auth Images ────────────────────────────────────────────────────

  const handleAuthImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenant?.id) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image file size must be less than 5MB");
      return;
    }

    const currentImages = branding.authImages || [];
    if (currentImages.length >= MAX_AUTH_IMAGES) {
      setError(`Maximum of ${MAX_AUTH_IMAGES} background images allowed`);
      return;
    }

    setIsUploadingAuthImage(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/tenants/${tenant.id}/branding/upload-auth-image`, formData);

      const updated = { ...branding, authImages: response.data.authImages };
      setBranding(updated);
      setPreviewBranding(updated);
      setSuccess("Background image uploaded!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: unknown) {
      const errorMessage =
        (err as any)?.response?.data?.message || (err as any)?.message || "Failed to upload image";
      setError(errorMessage);
    } finally {
      setIsUploadingAuthImage(false);
      if (authImageInputRef.current) authImageInputRef.current.value = "";
    }
  };

  const handleRemoveAuthImage = (index: number) => {
    const currentImages = [...(branding.authImages || [])];
    currentImages.splice(index, 1);
    const updated = { ...branding, authImages: currentImages };
    setBranding(updated);
    setPreviewBranding(updated);
  };

  const handleMoveAuthImage = useCallback(
    (index: number, direction: "up" | "down") => {
      const images = [...(branding.authImages || [])];
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= images.length) return;
      [images[index], images[target]] = [images[target], images[index]];
      const updated = { ...branding, authImages: images };
      setBranding(updated);
      setPreviewBranding(updated);
    },
    [branding]
  );

  // ─── Save / Reset ──────────────────────────────────────────────────

  const handleResetPreview = () => {
    setPreviewBranding(null);
    setError("");
  };

  const handleSave = async () => {
    if (!tenant?.id) return;

    setIsSaving(true);
    setError("");
    setSuccess("");

    try {
      await api.put(`/tenants/${tenant.id}/branding`, {
        primaryColor: branding.primaryColor || DEFAULT_COLOR,
        libraryFullName: branding.libraryFullName?.trim() || null,
        libraryAddress: branding.libraryAddress?.trim() || null,
        logoUrl: branding.logoUrl,
        faviconUrl: branding.faviconUrl,
        receiptSignatureUrl: branding.receiptSignatureUrl,
        receiptStampUrl: branding.receiptStampUrl,
        welcomeMessage: branding.welcomeMessage,
        customCss: branding.customCss,
        authImages: branding.authImages || [],
      });

      setSuccess("Branding settings saved successfully!");
      setPreviewBranding(null);
      applyBrandingToPage(branding);

      setTimeout(() => setSuccess(""), 2000);
    } catch (err: unknown) {
      const errorMessage =
        (err as any)?.response?.data?.message || (err as any)?.message || "Failed to save branding";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const applyBrandingToPage = (data: BrandingData) => {
    if (data.primaryColor) {
      document.documentElement.style.setProperty("--primary", data.primaryColor);
      const rgb = hexToRgb(data.primaryColor);
      if (rgb) {
        const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
        document.documentElement.style.setProperty("--primary", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
      }
    }

    if (data.faviconUrl) {
      let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = data.faviconUrl;
    }
  };

  useEffect(() => {
    if (previewBranding) {
      applyBrandingToPage(previewBranding);
    } else {
      applyBrandingToPage(branding);
    }
  }, [previewBranding, branding]);

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <h2 className="mb-4 text-2xl font-bold">Access Denied</h2>
            <p className="text-gray-600">You need Tenant Admin permissions to access this page.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const currentBranding = previewBranding || branding;
  const authImages = currentBranding.authImages || [];

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="Branding Settings"
          description="Customize your library's appearance with colors, logos, and messages."
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Branding" }]}
          showSearch={false}
          titleSize="md"
        />

        {error && (
          <div className="p-4 mb-4 text-red-800 border border-red-200 rounded-md bg-red-50">
            {error}
          </div>
        )}

        {success && (
          <div className="p-4 mb-4 text-green-800 border border-green-200 rounded-md bg-green-50">
            {success}
          </div>
        )}

        {isLoading ? (
          <Card className="p-6">
            <div className="text-center text-gray-600">Loading branding settings...</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Settings Panel */}
            <div className="space-y-6 lg:col-span-2">
              {/* Brand Color */}
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold">Brand Color</h2>
                <p className="mb-4 text-sm text-gray-600">
                  Choose your primary brand color. If no color is selected, the default blue will be
                  used.
                </p>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                  {BRAND_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => handleColorChange(color.value)}
                      className={`relative p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        currentBranding.primaryColor === color.value
                          ? "border-gray-900 scale-105"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    >
                      {currentBranding.primaryColor === color.value && (
                        <Check className="absolute w-5 h-5 text-white top-1 right-1 drop-shadow-lg" />
                      )}
                      <span className="sr-only">{color.label}</span>
                    </button>
                  ))}
                </div>
              </Card>

              <Card
                className="p-6"
                style={{ borderLeftColor: currentBranding.primaryColor || DEFAULT_COLOR }}
              >
                <h2 className="mb-1 text-xl font-semibold">Library name &amp; address</h2>
                <p className="mb-4 text-sm text-muted-foreground">
                  Used on printed patron cards (header) and can be reused elsewhere. The patron-type
                  strip on physical cards uses your <strong>brand color</strong> from above.
                </p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="library-full-name">Library full name</Label>
                    <Input
                      id="library-full-name"
                      value={currentBranding.libraryFullName ?? ""}
                      onChange={(e) => handleLibraryFullNameChange(e.target.value)}
                      placeholder="e.g. Tai Solarin University of Education Library"
                      maxLength={200}
                    />
                    <p className="text-xs text-muted-foreground">
                      If empty, your tenant short name is used on cards.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="library-address">Library address</Label>
                    <Textarea
                      id="library-address"
                      value={currentBranding.libraryAddress ?? ""}
                      onChange={(e) => handleLibraryAddressChange(e.target.value)}
                      placeholder="e.g. Ijagun, Ijebu-Ode"
                      rows={3}
                      maxLength={500}
                      className="resize-y min-h-[80px]"
                    />
                    <p className="text-xs text-muted-foreground">
                      Shown as the second line under the library name on printed cards (line breaks
                      allowed).
                    </p>
                  </div>
                </div>
              </Card>

              {/* Logo Upload */}
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold">Logo</h2>
                <div className="space-y-4">
                  {currentBranding.logoUrl && (
                    <div className="relative inline-block">
                      <img
                        src={currentBranding.logoUrl}
                        alt="Logo preview"
                        className="object-contain max-w-xs max-h-32"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveLogo}
                        className="absolute top-0 right-0 text-white bg-red-500 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isUploadingLogo}
                        onClick={() => logoInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploadingLogo ? "Uploading..." : "Upload Logo"}
                      </Button>
                    </Label>
                    <p className="mt-2 text-xs text-gray-500">Recommended: PNG or SVG, max 5MB</p>
                  </div>
                </div>
              </Card>

              {/* Favicon Upload */}
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold">Favicon</h2>
                <div className="space-y-4">
                  {currentBranding.faviconUrl && (
                    <div className="relative inline-block">
                      <img
                        src={currentBranding.faviconUrl}
                        alt="Favicon preview"
                        className="object-contain w-16 h-16"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveFavicon}
                        className="absolute top-0 right-0 text-white bg-red-500 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div>
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFaviconUpload}
                      className="hidden"
                      id="favicon-upload"
                    />
                    <Label htmlFor="favicon-upload" className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isUploadingFavicon}
                        onClick={() => faviconInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploadingFavicon ? "Uploading..." : "Upload Favicon"}
                      </Button>
                    </Label>
                    <p className="mt-2 text-xs text-gray-500">
                      Recommended: ICO or PNG, 32x32px, max 1MB
                    </p>
                  </div>
                </div>
              </Card>

              {/* Payment receipt — signature & stamp */}
              <Card className="p-6">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Stamp className="w-5 h-5 text-gray-500" aria-hidden />
                  <h2 className="text-xl font-semibold">Payment receipt</h2>
                </div>
                <p className="mb-6 text-sm text-gray-600">
                  Upload a signature and optional official stamp. They appear on printed payment
                  receipts for your library so patrons can verify authenticity.
                </p>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="p-4 space-y-3 border border-gray-200 rounded-lg bg-gray-50/50">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <PenLine className="w-4 h-4" aria-hidden />
                      Authorized signature
                    </div>
                    {currentBranding.receiptSignatureUrl && (
                      <div className="relative inline-block">
                        <img
                          src={currentBranding.receiptSignatureUrl}
                          alt="Receipt signature preview"
                          className="object-contain object-left max-w-full max-h-24"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveReceiptSignature}
                          className="absolute w-8 h-8 p-0 text-white bg-red-500 -top-1 -right-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <input
                      ref={receiptSignatureInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleReceiptSignatureUpload}
                      className="hidden"
                      id="receipt-signature-upload"
                    />
                    <Label htmlFor="receipt-signature-upload" className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isUploadingReceiptSignature}
                        onClick={() => receiptSignatureInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploadingReceiptSignature ? "Uploading..." : "Upload signature"}
                      </Button>
                    </Label>
                    <p className="text-xs text-gray-500">
                      PNG or JPG, transparent background recommended
                    </p>
                  </div>
                  <div className="p-4 space-y-3 border border-gray-200 rounded-lg bg-gray-50/50">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <Stamp className="w-4 h-4" aria-hidden />
                      Official stamp
                    </div>
                    {currentBranding.receiptStampUrl && (
                      <div className="relative inline-block">
                        <img
                          src={currentBranding.receiptStampUrl}
                          alt="Receipt stamp preview"
                          className="object-contain object-left max-w-full max-h-28"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveReceiptStamp}
                          className="absolute w-8 h-8 p-0 text-white bg-red-500 -top-1 -right-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <input
                      ref={receiptStampInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleReceiptStampUpload}
                      className="hidden"
                      id="receipt-stamp-upload"
                    />
                    <Label htmlFor="receipt-stamp-upload" className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isUploadingReceiptStamp}
                        onClick={() => receiptStampInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploadingReceiptStamp ? "Uploading..." : "Upload stamp"}
                      </Button>
                    </Label>
                    <p className="text-xs text-gray-500">Circular stamps work well; max 5MB</p>
                  </div>
                </div>
              </Card>

              {/* ── Auth Background Images ──────────────────────── */}
              <Card className="p-6">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h2 className="text-xl font-semibold">Login Background Images</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      These images appear as a rotating slider on your library's login and
                      registration pages. If none are uploaded, default library images will be used.
                    </p>
                  </div>
                  <span className="mt-1 ml-4 text-xs font-medium text-gray-400 whitespace-nowrap">
                    {authImages.length} / {MAX_AUTH_IMAGES}
                  </span>
                </div>

                {/* Image Grid */}
                {authImages.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 mt-4 sm:grid-cols-3">
                    {authImages.map((url, index) => (
                      <AuthImageCard
                        key={`${url}-${index}`}
                        url={url}
                        index={index}
                        total={authImages.length}
                        onRemove={() => handleRemoveAuthImage(index)}
                        onMove={(dir) => handleMoveAuthImage(index, dir)}
                      />
                    ))}
                  </div>
                )}

                {/* Upload button */}
                <div className="mt-4">
                  <input
                    ref={authImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAuthImageUpload}
                    className="hidden"
                    id="auth-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploadingAuthImage || authImages.length >= MAX_AUTH_IMAGES}
                    onClick={() => authImageInputRef.current?.click()}
                    className="w-full h-auto py-4 border-2 border-dashed"
                  >
                    {isUploadingAuthImage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : authImages.length >= MAX_AUTH_IMAGES ? (
                      <>Maximum images reached</>
                    ) : (
                      <>
                        <ImagePlus className="w-4 h-4 mr-2" />
                        Add Background Image
                      </>
                    )}
                  </Button>
                  <p className="mt-2 text-xs text-gray-500">
                    Recommended: landscape orientation, at least 1920x1080px, JPG or PNG, max 5MB
                  </p>
                </div>

                {authImages.length > 0 && previewBranding && (
                  <p className="flex items-center gap-1 mt-3 text-xs text-amber-600">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Remember to save changes to apply reordering or removals
                  </p>
                )}
              </Card>

              {/* Welcome Message */}
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold">Welcome Message</h2>
                <Label htmlFor="welcome-message" className="block mb-2 text-sm text-gray-600">
                  Custom message displayed on login page (optional)
                </Label>
                <Input
                  id="welcome-message"
                  value={currentBranding.welcomeMessage || ""}
                  onChange={(e) => handleWelcomeMessageChange(e.target.value)}
                  placeholder="Welcome to our library..."
                  maxLength={500}
                />
                <p className="mt-2 text-xs text-gray-500">
                  {currentBranding.welcomeMessage?.length || 0} / 500 characters
                </p>
              </Card>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-1">
              <Card className="sticky p-6 top-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Preview</h2>
                  {previewBranding && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetPreview}
                      className="text-xs"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div
                    className="p-6 text-white rounded-lg"
                    style={{ backgroundColor: currentBranding.primaryColor }}
                  >
                    <h3 className="mb-2 text-lg font-bold">Sample Header</h3>
                    <p className="text-sm opacity-90">This is how your brand color will appear</p>
                  </div>

                  {currentBranding.logoUrl && (
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="mb-2 text-sm text-gray-600">Logo Preview:</p>
                      <img
                        src={currentBranding.logoUrl}
                        alt="Logo"
                        className="object-contain max-w-full max-h-20"
                      />
                    </div>
                  )}

                  {/* Login Page Preview */}
                  {authImages.length > 0 && (
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <p className="px-4 pt-3 pb-2 text-sm text-gray-600">Login Page Preview:</p>
                      <div className="relative h-32">
                        <img
                          src={authImages[0]}
                          alt="Login background"
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
                        <div className="absolute text-white bottom-3 left-4">
                          <p className="text-xs font-bold tracking-wider uppercase opacity-60">
                            {tenant?.name || "Your Library"}
                          </p>
                          <p className="text-sm font-semibold mt-0.5">Your library, organized.</p>
                        </div>
                        {authImages.length > 1 && (
                          <div className="absolute flex gap-1 bottom-3 right-4">
                            {authImages.map((_, i) => (
                              <div
                                key={i}
                                className={`h-1 rounded-full ${
                                  i === 0 ? "w-4 bg-white/80" : "w-1.5 bg-white/30"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {currentBranding.welcomeMessage && (
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="mb-2 text-sm text-gray-600">Welcome Message:</p>
                      <p className="text-sm">{currentBranding.welcomeMessage}</p>
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <Button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="w-full gap-2"
                      style={{ backgroundColor: currentBranding.primaryColor }}
                    >
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Save Changes
                    </Button>
                    {previewBranding && (
                      <p className="mt-2 text-xs text-center text-gray-500">
                        You have unsaved changes
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

// ─── Auth Image Card ──────────────────────────────────────────────────────

function AuthImageCard({
  url,
  index,
  total,
  onRemove,
  onMove,
}: {
  url: string;
  index: number;
  total: number;
  onRemove: () => void;
  onMove: (direction: "up" | "down") => void;
}) {
  return (
    <div className="relative overflow-hidden border border-gray-200 rounded-lg group bg-gray-50">
      <img
        src={url}
        alt={`Background ${index + 1}`}
        className="object-cover w-full h-28 sm:h-32"
        loading="lazy"
      />

      {/* Overlay on hover */}
      <div className="absolute inset-0 transition-colors duration-200 bg-black/0 group-hover:bg-black/40" />

      {/* Order badge */}
      <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
        {index + 1}
      </div>

      {/* Controls — visible on hover */}
      <div className="absolute flex items-center gap-1 transition-opacity duration-200 opacity-0 top-2 right-2 group-hover:opacity-100">
        <button
          onClick={() => onMove("up")}
          disabled={index === 0}
          className="p-1 rounded cursor-pointer bg-white/90 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Move left"
        >
          <ChevronUp className="h-3.5 w-3.5 text-gray-700" />
        </button>
        <button
          onClick={() => onMove("down")}
          disabled={index === total - 1}
          className="p-1 rounded cursor-pointer bg-white/90 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
          title="Move right"
        >
          <ChevronDown className="h-3.5 w-3.5 text-gray-700" />
        </button>
        <button
          onClick={onRemove}
          className="p-1 rounded cursor-pointer bg-red-500/90 hover:bg-red-600"
          title="Remove image"
        >
          <Trash2 className="h-3.5 w-3.5 text-white" />
        </button>
      </div>

      {/* Grip indicator */}
      <div className="absolute transition-opacity -translate-x-1/2 opacity-0 bottom-2 left-1/2 group-hover:opacity-60">
        <GripVertical className="w-4 h-4 text-white" />
      </div>
    </div>
  );
}

export default BrandingSettingsPage;
